'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * DateRangeFilter — THE date-range control for report toolbars and page
 * headers: presets on the left, a range calendar on the right, one line of text
 * on the trigger.
 *
 * Speaks `YYYY-MM-DD` strings, INCLUSIVE at both ends. Timestamps are
 * deliberately not modelled, and that is the load-bearing decision rather than
 * a simplification: a report filtered by "which day did this happen" should not
 * depend on what time of day the user happened to set it, and a control that
 * carried times would have to answer "does 3 Aug mean from midnight or from
 * now" differently for every caller.
 *
 * APIs that want epoch seconds are served by `isoRangeToUnix` / `unixRangeToISO`
 * below rather than by a second value shape in here. The conversion has a rule
 * in it (lower bound at the START of its day, upper bound at the END of its
 * day) and a trap (`new Date('2026-08-10')` parses as UTC), so it lives beside
 * the local-parts helpers instead of being re-derived per caller.
 */

export interface DateRangeFilterProps {
  /** Inclusive lower bound, `YYYY-MM-DD`. */
  from?: string | null;
  /** Inclusive upper bound, `YYYY-MM-DD`. */
  to?: string | null;
  onChange: (from: string | null, to: string | null) => void;
  /**
   * Whether "no range" is a state the caller can actually serve. Defaults to
   * true: the control offers "All time" and its clear button unsets the range.
   *
   * A caller whose every number is windowed (the Reports page: each figure has
   * a prior period to compare with, and an unbounded query is not one the
   * server will run) passes `false`. The control then never says "All time"
   * and never emits a null bound: the preset is dropped, the clear button
   * becomes a reset to the last 30 days (shown only when the range differs
   * from it), and a half-picked custom range stays inside the control until
   * its second click instead of reaching `onChange` as `(from, null)`.
   */
  allowAllTime?: boolean;
  /**
   * Layout for the control as a whole — WIDTH. The trigger fills it.
   *
   * There is deliberately no height knob. Both surfaces that use this sit in a
   * row of SelectTriggers and SearchInputs, all of which render at the standard
   * h-9, so the Button's own default is already the only correct answer — and a
   * `size` prop would mostly be an opportunity to get it wrong. (It nearly was:
   * in this design system `sm` is h-8, not h-9.)
   */
  className?: string;
  /** Announced to screen readers; the visible text is the range itself. */
  'aria-label'?: string;
}

/**
 * A Date → `YYYY-MM-DD` using its LOCAL calendar parts.
 *
 * Never `toISOString().slice(0,10)`, which converts to UTC first: at IST
 * (UTC+5:30) a date picked as the 10th serialises as the 9th, so "Today" would
 * quietly ask the server for yesterday. The user picked a square on a calendar;
 * that square's own numbers are the answer.
 */
export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * `YYYY-MM-DD` → local Date, or undefined. Parsed part-wise for the same reason
 * `toISODate` formats that way.
 *
 * The round-trip check is not belt-and-braces: `2026-13-45` matches the shape,
 * and `new Date(2026, 12, 45)` rolls it forward to 14 Feb 2027 rather than
 * refusing. Without the check the control would confidently label a range the
 * SERVER discards as malformed, which is the worst of both — a filter that
 * reads as applied and isn't.
 */
function fromISODate(s?: string | null): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(y, mo - 1, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) return undefined;
  return d;
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

// --- Epoch-second APIs ---------------------------------------------------
//
// Some of our filters bind to columns stored as Unix SECONDS rather than dates
// (`job_applications.submitted_at` is a bigint, kept that way so the index is
// usable). Those callers still want a control that talks in days, so the
// conversion lives here rather than in each page.
//
// Two things make it worth centralising. The rule: an inclusive day range
// becomes [start of the first day, end of the last day] — using start-of-day for
// the UPPER bound silently drops everything that happened after midnight on the
// end date, which is almost all of it. And the trap: `new Date('2026-08-10')`
// parses as UTC midnight while `new Date(2026, 7, 10)` is local, so a hand-rolled
// conversion is off by the viewer's UTC offset — for IST that lands the bound on
// the wrong day.

/** An inclusive `YYYY-MM-DD` range → the inclusive Unix-second bounds for it. */
export function isoRangeToUnix(
  from?: string | null,
  to?: string | null,
): { from: number | null; to: number | null } {
  const f = fromISODate(from);
  const t = fromISODate(to);
  if (t) t.setHours(23, 59, 59, 999);
  return {
    from: f ? Math.floor(f.getTime() / 1000) : null,
    to: t ? Math.floor(t.getTime() / 1000) : null,
  };
}

/**
 * …and back, for rendering a stored range in the control.
 *
 * Lossy on purpose: a bound carrying a time of day collapses to the day it falls
 * on. That is the same collapse the control performs on every edit, so a value
 * it round-trips is stable — only a hand-written URL can carry a mid-day bound,
 * and widening it to that whole day is the reading a day-granular control can
 * honestly render.
 */
export function unixRangeToISO(
  from?: number | null,
  to?: number | null,
): { from: string | null; to: string | null } {
  const at = (s?: number | null): string | null =>
    typeof s === 'number' && Number.isFinite(s) ? toISODate(new Date(s * 1000)) : null;
  return { from: at(from), to: at(to) };
}

/**
 * Ranges are INCLUSIVE of both ends, so "last 7 days" starts 6 days back and
 * ends today — 7 dates, not 8. The off-by-one matters here: these labels get
 * read as claims about a period, and a "7 days" tile covering 8 is wrong in the
 * direction nobody checks.
 */
interface Preset {
  label: string;
  range: () => [Date, Date];
}

/** Also where a caller that cannot go unbounded resets to (see `allowAllTime`),
 *  so the preset button and the reset button can never name different windows. */
const LAST_30_DAYS: Preset = { label: 'Last 30 days', range: () => [daysAgo(29), new Date()] };

const PRESETS: Preset[] = [
  { label: 'Today', range: () => [new Date(), new Date()] },
  { label: 'Yesterday', range: () => [daysAgo(1), daysAgo(1)] },
  { label: 'Last 7 days', range: () => [daysAgo(6), new Date()] },
  LAST_30_DAYS,
  { label: 'Last 90 days', range: () => [daysAgo(89), new Date()] },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return [new Date(now.getFullYear(), now.getMonth(), 1), now];
    },
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      return [
        new Date(now.getFullYear(), now.getMonth() - 1, 1),
        // Day 0 of this month is the last day of the previous one.
        new Date(now.getFullYear(), now.getMonth(), 0),
      ];
    },
  },
];

/** The reset window as `YYYY-MM-DD`, today included. */
function lastDaysWindow(): { from: string; to: string } {
  const [start, end] = LAST_30_DAYS.range();
  return { from: toISODate(start), to: toISODate(end) };
}

/** What an unset range is called, everywhere. */
const ALL_TIME = 'All time';

/** Trigger text when a bounded caller somehow has no range yet. */
const NO_RANGE_BOUNDED = 'Select dates';

/**
 * A range in words. Same year on both ends is said once, not twice.
 *
 * Exported because this control is not the only thing that has to name the
 * applied range — a filter summary elsewhere on the page names it too, and two
 * hand-rolled formatters is how one ends up saying "10 Aug" while the other
 * says "10 Aug 2026" for the same filter.
 */
export function formatDateRange(from?: string | null, to?: string | null): string {
  const f = fromISODate(from);
  const t = fromISODate(to);
  // One wording, not a per-caller label. Two surfaces showing one control that
  // disagree about what "no range" is called are two controls again.
  if (!f && !t) return ALL_TIME;
  if (f && t) {
    if (toISODate(f) === toISODate(t)) return format(f, 'd MMM yyyy');
    return f.getFullYear() === t.getFullYear()
      ? `${format(f, 'd MMM')} – ${format(t, 'd MMM yyyy')}`
      : `${format(f, 'd MMM yyyy')} – ${format(t, 'd MMM yyyy')}`;
  }
  return f ? `From ${format(f, 'd MMM yyyy')}` : `Until ${format(t!, 'd MMM yyyy')}`;
}

export function DateRangeFilter({
  from,
  to,
  onChange,
  allowAllTime = true,
  className,
  'aria-label': ariaLabel = 'Date range',
}: DateRangeFilterProps) {
  const [open, setOpen] = React.useState(false);
  // A custom range being picked, bounded callers only (see `allowAllTime`).
  const [pending, setPending] = React.useState<DateRange | undefined>(undefined);
  const fromDate = fromISODate(from);
  const toDate = fromISODate(to);
  const isSet = Boolean(fromDate || toDate);

  // Only a bounded caller has a reset window; the four unbounded pages never
  // compute it.
  const reset = allowAllTime ? null : lastDaysWindow();
  // Unbounded callers clear whenever anything is set; bounded ones reset, and
  // only when there is something to reset (no permanently dead button).
  const showClear = reset ? isSet && !(from === reset.from && to === reset.to) : isSet;

  const applyPreset = (range: () => [Date, Date]) => {
    const [start, end] = range();
    setPending(undefined);
    onChange(toISODate(start), toISODate(end));
    setOpen(false);
  };

  // The calendar reports a partial range (from set, to still undefined) on the
  // first of the two clicks. Passing that through immediately is what makes the
  // list narrow to "from this day onward" while the user is mid-selection —
  // deliberate, because the alternative is a control that looks stuck until the
  // second click. The popover stays open until they finish.
  const handleSelect = (range: DateRange | undefined, triggerDate: Date) => {
    if (allowAllTime) {
      onChange(range?.from ? toISODate(range.from) : null, range?.to ? toISODate(range.to) : null);
      return;
    }
    // Bounded: the first click starts a NEW range (the library would otherwise
    // nudge one end of the committed one), the second completes it, in either
    // order, and only a complete range leaves the control.
    const start = pending?.from && !pending.to ? pending.from : undefined;
    if (!start) {
      setPending({ from: triggerDate, to: undefined });
      return;
    }
    const [first, last] = start <= triggerDate ? [start, triggerDate] : [triggerDate, start];
    setPending(undefined);
    onChange(toISODate(first), toISODate(last));
  };

  const handleClear = () => {
    setPending(undefined);
    if (reset) onChange(reset.from, reset.to);
    else onChange(null, null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // A half-picked range is abandoned on close: the trigger keeps naming
        // the range the report actually holds.
        if (!next) setPending(undefined);
      }}
    >
      {/* inline-flex, so with no width class the control sizes to its label —
          and a caller passing `w-full sm:w-[180px]` gets a trigger that fills
          it, because the trigger is flex-1 inside. */}
      <div className={cn('inline-flex items-center', className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-label={ariaLabel}
            className={cn('min-w-0 flex-1 justify-start font-normal', showClear && 'rounded-r-none border-r-0')}
          >
            <CalendarIcon className="mr-1.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{allowAllTime || isSet ? formatDateRange(from, to) : NO_RANGE_BOUNDED}</span>
          </Button>
        </PopoverTrigger>
        {/* Only rendered once there is something to clear (or, bounded, to
            reset), so the default state is a single clean button rather than a
            control with a permanently dead X. */}
        {showClear && (
          <Button
            variant="outline"
            aria-label={reset ? `Reset date range to ${LAST_30_DAYS.label}` : 'Clear date range'}
            onClick={handleClear}
            className="shrink-0 rounded-l-none px-2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>
      <PopoverContent align="end" className="w-auto p-0">
        <div className="flex flex-col sm:flex-row">
          <div className="flex gap-0.5 overflow-x-auto border-b p-2 sm:w-[140px] sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => applyPreset(p.range)}
                className="h-auto shrink-0 justify-start px-2 py-1.5 text-sm sm:w-full"
              >
                {p.label}
              </Button>
            ))}
            {allowAllTime && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { onChange(null, null); setOpen(false); }}
                className="h-auto shrink-0 justify-start px-2 py-1.5 text-sm text-muted-foreground sm:w-full"
              >
                {ALL_TIME}
              </Button>
            )}
          </div>
          {/* `relative` is load-bearing. Calendar positions its month-nav
              chevrons `absolute left-1 / right-1`, which otherwise resolve
              against the whole popover and land on top of the preset column. */}
          <div className="relative">
            <Calendar
              mode="range"
              selected={!allowAllTime && pending ? pending : { from: fromDate, to: toDate }}
              onSelect={handleSelect}
              defaultMonth={fromDate ?? toDate}
              numberOfMonths={1}
              // A report can only cover calls that already happened.
              disabled={{ after: new Date() }}
              autoFocus
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
